import React, { useState } from 'react';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DataTable } from '@/components/shared/DataTable';
import { FormDialog, FormField } from '@/components/shared/FormDialog';
import { TypeFilter } from '@/components/shared/SearchFilter';
import { useRooms } from '@/hooks/useRooms';
import { useRoomTypes } from '@/hooks/useRoomTypes';

/**
 * Rooms Management Component
 * Displays rooms table with CRUD operations
 */
export const RoomsManagement = ({ API }) => {
  const {
    rooms,
    loading,
    createRoom,
    updateRoom,
    deleteRoom,
  } = useRooms(API);

  const { roomTypes, getRoomTypeName } = useRoomTypes(API);

  const [showDialog, setShowDialog] = useState(false);
  const [editingRoom, setEditingRoom] = useState(null);
  const [typeFilter, setTypeFilter] = useState('all');
  const [formData, setFormData] = useState({
    room_number: '',
    room_type_id: '',
    status: 'available',
    beds24_room_id: null,
    beds24_unit_id: null
  });

  // Filter rooms by type
  const filteredRooms = typeFilter === 'all' 
    ? rooms 
    : rooms.filter(r => r.room_type_id === typeFilter);

  // Table columns definition
  const columns = [
    {
      key: 'room_number',
      label: 'Room',
      sortable: true,
      render: (value) => (
        <div className="flex items-center">
          <div className="h-10 w-10 flex-shrink-0 bg-blue-100 rounded flex items-center justify-center text-blue-600 font-bold">
            {value}
          </div>
        </div>
      )
    },
    {
      key: 'room_type_id',
      label: 'Type',
      render: (value) => (
        <div className="text-sm font-semibold text-gray-900">
          {getRoomTypeName(value)}
        </div>
      )
    },
    {
      key: 'beds24_room_id',
      label: 'Beds24 Mapping',
      render: (roomId, row) => {
        if (roomId) {
          return (
            <div className="flex flex-col gap-1">
              <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-700">
                Room: {roomId}
              </span>
              {row.beds24_unit_id && (
                <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-700">
                  Unit: {row.beds24_unit_id}
                </span>
              )}
            </div>
          );
        }
        return <span className="text-xs text-gray-400">Not mapped</span>;
      }
    },
    {
      key: 'status',
      label: 'Status',
      render: (value) => (
        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
          value === 'available' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
        }`}>
          {value}
        </span>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      className: 'text-right',
      render: (_, room) => (
        <div className="flex justify-end gap-2">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => handleEdit(room)}
          >
            <Edit className="w-4 h-4" />
          </Button>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => handleDelete(room.id)}
          >
            <Trash2 className="w-4 h-4 text-red-500" />
          </Button>
        </div>
      )
    }
  ];

  const handleEdit = (room) => {
    setEditingRoom(room.id);
    setFormData({
      room_number: room.room_number,
      room_type_id: room.room_type_id || '',
      status: room.status,
      beds24_room_id: room.beds24_room_id || null,
      beds24_unit_id: room.beds24_unit_id || null
    });
    setShowDialog(true);
  };

  const handleDelete = async (roomId) => {
    if (!window.confirm('Are you sure you want to delete this room?')) return;
    await deleteRoom(roomId);
  };

  const handleSubmit = async () => {
    const data = {
      ...formData,
      beds24_room_id: formData.beds24_room_id || null,
      beds24_unit_id: formData.beds24_unit_id || null
    };

    if (editingRoom) {
      await updateRoom(editingRoom, data);
    } else {
      await createRoom(data);
    }
    
    setShowDialog(false);
    resetForm();
  };

  const resetForm = () => {
    setEditingRoom(null);
    setFormData({
      room_number: '',
      room_type_id: '',
      status: 'available',
      beds24_room_id: null,
      beds24_unit_id: null
    });
  };

  const handleDialogClose = (open) => {
    setShowDialog(open);
    if (!open) resetForm();
  };

  // Get room types with counts for filter
  const typesWithCounts = roomTypes.map(type => ({
    ...type,
    count: rooms.filter(r => r.room_type_id === type.id).length
  }));

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-4">
          <div>
            <h2 className="text-2xl font-bold">Manage Rooms</h2>
            <p className="text-sm text-gray-600 mt-1">
              {filteredRooms.length} room{filteredRooms.length !== 1 ? 's' : ''}
              {typeFilter !== 'all' ? ' filtered' : ' total'}
            </p>
          </div>
          
          {/* Type Filter */}
          <TypeFilter
            types={typesWithCounts}
            selectedType={typeFilter}
            onTypeChange={setTypeFilter}
            label="Filter by type:"
            allLabel="All Room Types"
          />
        </div>

        <Button 
          className="bg-gradient-to-r from-blue-600 to-indigo-600"
          onClick={() => setShowDialog(true)}
        >
          <Plus className="w-5 h-5 mr-2" />
          Add Room
        </Button>
      </div>

      {/* Rooms Table */}
      <DataTable
        columns={columns}
        data={filteredRooms}
        testIdPrefix="room"
        emptyState={
          <div className="text-center text-gray-500">
            <p>No rooms added yet. Click "Add Room" to get started.</p>
          </div>
        }
      />

      {/* Room Form Dialog */}
      <FormDialog
        open={showDialog}
        onOpenChange={handleDialogClose}
        title={editingRoom ? 'Edit Room' : 'Add New Room'}
        onSubmit={handleSubmit}
        submitLabel={editingRoom ? 'Update Room' : 'Create Room'}
        size="lg"
      >
        <FormField label="Room Number" required htmlFor="room_number">
          <Input
            id="room_number"
            placeholder="e.g., 105, 106"
            value={formData.room_number}
            onChange={(e) => setFormData({ ...formData, room_number: e.target.value })}
            required
          />
        </FormField>

        <FormField label="Room Type" required htmlFor="room_type_id">
          <select
            id="room_type_id"
            value={formData.room_type_id}
            onChange={(e) => setFormData({ ...formData, room_type_id: e.target.value })}
            required
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          >
            <option value="">Select room type</option>
            {roomTypes.map(type => (
              <option key={type.id} value={type.id}>
                {type.name} - €{type.base_price?.toFixed(2) || '0.00'}/night
              </option>
            ))}
          </select>
        </FormField>

        <FormField label="Status" required htmlFor="status">
          <select
            id="status"
            value={formData.status}
            onChange={(e) => setFormData({ ...formData, status: e.target.value })}
            required
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          >
            <option value="available">Available</option>
            <option value="unavailable">Unavailable</option>
          </select>
        </FormField>

        <FormField label="Beds24 Room ID" htmlFor="beds24_room_id">
          <Input
            id="beds24_room_id"
            type="number"
            placeholder="Enter Beds24 room ID"
            value={formData.beds24_room_id || ''}
            onChange={(e) => setFormData({ 
              ...formData, 
              beds24_room_id: e.target.value ? parseInt(e.target.value) : null 
            })}
          />
          <p className="text-xs text-gray-500 mt-1">
            Corresponding room ID from your Beds24 account
          </p>
        </FormField>

        <FormField label="Beds24 Unit ID" htmlFor="beds24_unit_id">
          <Input
            id="beds24_unit_id"
            type="number"
            placeholder="Enter Beds24 unit ID"
            value={formData.beds24_unit_id || ''}
            onChange={(e) => setFormData({ 
              ...formData, 
              beds24_unit_id: e.target.value ? parseInt(e.target.value) : null 
            })}
          />
          <p className="text-xs text-gray-500 mt-1">
            Unit number within the Beds24 room type
          </p>
        </FormField>
      </FormDialog>
    </div>
  );
};